import {
  getDepartureAndArrivalDataFromReservation,
  getItemsFromReservation,
  getNumOfPriceCategorySubTypesFromItems,
  getPhoneProp,
  getRefundStatusesFromReservation,
  getSailRefIdsFromReservation,
  getVehiclesFromItemsAndReservation,
  getVesselTitleFromReservation,
  getCancellationInfoFromReservation,
  getAllowanceFromReservation,
} from '@/root/shared-helpers'
import { Reservation, ReservationItem, TicketData } from '@/root/shared-types/reservation/reservation-types'

export function getPrintedTicketData(staticReservation: Reservation | undefined): {
  reservation: TicketData | undefined
  error: Error | undefined
  isLoading: boolean
} {
  const { reservation, error, isLoading } = {
    reservation: staticReservation,
    error: undefined,
    isLoading: false,
  }

  if (!reservation) {
    return { error, isLoading, reservation }
  }

  const { reservationId, reservationOwner, guests } = reservation

  const {
    departureThere,
    departurePortThere,
    arrivalThere,
    arrivalPortThere,
    departureBack,
    departurePortBack,
    arrivalBack,
    arrivalPortBack,
  } = getDepartureAndArrivalDataFromReservation(reservation)

  const { itemsThere, itemsBack } = getItemsFromReservation(reservation)

  const residentGuests = guests?.filter((guest) => guest.personalIdentificationNumber) ?? []

  const getResidentsByItems = (items: ReservationItem[]) => {
    const seqNs = new Set(items.flatMap((item) => item.ownerSeqNs ?? []))
    return residentGuests.filter((guest) => seqNs.has(guest.seqN))
  }

  const residentsThere = getResidentsByItems(itemsThere)
  const residentsBack = getResidentsByItems(itemsBack)

  const { vesselTitleBack, vesselTitleThere } = getVesselTitleFromReservation(reservation)

  const { numOfCars: numOfCarsThere, numOfPassengers: numOfPassengersThere } =
    getNumOfPriceCategorySubTypesFromItems(itemsThere)

  const { numOfCars: numOfCarsBack, numOfPassengers: numOfPassengersBack } =
    getNumOfPriceCategorySubTypesFromItems(itemsBack)

  const vehiclesThere = getVehiclesFromItemsAndReservation(reservation, itemsThere)
  const vehiclesBack = getVehiclesFromItemsAndReservation(reservation, itemsBack)

  const { sailRefIdThere, sailRefIdBack } = getSailRefIdsFromReservation(reservation)

  const { isRefundThere, isRefundBack, isCancelledThere, isCancelledBack } =
    getRefundStatusesFromReservation(reservation)

  const { there: cancellationThere, back: cancellationBack } = getCancellationInfoFromReservation(reservation)

  const { there: allowanceThere, back: allowanceBack } = getAllowanceFromReservation(reservation)

  const { expireAt } = reservation

  const { email = '', fullName = '', lastName = '', firstName = '', companyInfo } = reservationOwner || {}

  const intlCode = getPhoneProp('intlCode')(reservationOwner)
  const phoneNumber = getPhoneProp('phoneNumber')(reservationOwner)

  const {
    feeAmount = 0,
    amountWithoutTaxes = 0,
    taxAmount = 0,
    amountFormatted = '',
    currency = 'EUR',
    amount = 0,
    tax = 24,
  } = reservation?.paymentInfo?.totalPrice || {}

  return {
    reservation: {
      there: {
        departure: departureThere,
        departurePort: departurePortThere,
        arrival: arrivalThere,
        arrivalPort: arrivalPortThere,
        items: itemsThere,
        vesselTitle: vesselTitleThere,
        numOfCars: numOfCarsThere,
        numOfPassengers: numOfPassengersThere,
        vehicles: vehiclesThere,
        sailRefId: sailRefIdThere,
        isRefund: isRefundThere,
        isCancelled: isCancelledThere,
        residents: residentsThere,
        cancellationInfo: cancellationThere,
        allowance: allowanceThere,
      },
      back: {
        departure: departureBack,
        departurePort: departurePortBack,
        arrival: arrivalBack,
        arrivalPort: arrivalPortBack,
        items: itemsBack,
        vesselTitle: vesselTitleBack,
        numOfCars: numOfCarsBack,
        numOfPassengers: numOfPassengersBack,
        vehicles: vehiclesBack,
        sailRefId: sailRefIdBack,
        isRefund: isRefundBack,
        isCancelled: isCancelledBack,
        residents: residentsBack,
        cancellationInfo: cancellationBack,
        allowance: allowanceBack,
      },
      customerInfo: {
        email,
        fullName,
        lastName,
        firstName,
        phoneNumber: phoneNumber ? `+${intlCode} ${phoneNumber}` : '',
        companyInfo,
      },
      reservationId,
      isRoundTrip: reservation.sailPackages?.length === 2,
      status: reservation.status,
      terms: reservation.terms ?? [],
      paymentInfo: reservation.paymentInfo,
      expireAt,
      currency,
      amount,
      totalToPay: amountFormatted,
      feeAmount,
      charges: {
        quantity: 1,
        price: amountWithoutTaxes,
        vat: taxAmount,
        subtotal: amount,
        tax,
      },
      route: reservation.routes[0],
      token: reservation.token,
    },
    error,
    isLoading,
  }
}

export function usePrintedTicketData(staticReservation: Reservation | undefined): {
  reservation: TicketData | undefined
  error: Error | undefined
  isLoading: boolean
} {
  return getPrintedTicketData(staticReservation)
}
